import { USER_SEARCH } from '@/config/api/api-routes';
import { http } from '@/shared/api/http';
import type { ResultApi } from '@/shared/types/result-api';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { UserSearchReq, UserSearchRes } from '../models/user-search-dto';

export interface UseInfiniteUserSearchOptions {
  searchTerm?: string;
  enabled?: boolean;
  pageSize?: number;
}

export interface InfiniteUserSearchData {
  pages: UserSearchRes[];
  pageParams: UserSearchReq[];
}

export const QK_USERS_INFINITE_SEARCH = 'users-infinite-search';
export const USERS_PAGE_SIZE = 20;

const searchUsersInfinite = async (params: UserSearchReq): Promise<UserSearchRes> => {
  const response = await http.get<ResultApi<UserSearchRes>>(USER_SEARCH, { params });
  if (!response.data.data) {
    throw new Error('Failed to search users');
  }
  return response.data.data;
};

export function useInfiniteUserSearch({
  searchTerm = '',
  enabled = true,
  pageSize = USERS_PAGE_SIZE
}: UseInfiniteUserSearchOptions) {
  const query = useInfiniteQuery({
    queryKey: [QK_USERS_INFINITE_SEARCH, searchTerm],
    queryFn: async ({ pageParam }: { pageParam: UserSearchReq }) => {
      return await searchUsersInfinite(pageParam);
    },
    initialPageParam: {
      searchTerm,
      page: 1,
      pageSize
    } as UserSearchReq,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage) return undefined;
      return {
        searchTerm,
        page: lastPage.page + 1,
        pageSize
      } as UserSearchReq;
    },
    enabled: enabled && (!searchTerm || searchTerm.trim().length > 2),
    staleTime: 30000,
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
    maxPages: 10
  });

  // Load more users (scroll down)
  const loadMoreUsers = async () => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    await query.fetchNextPage();
  };

  // Get all users flattened
  const allUsers = query.data?.pages.flatMap((page) => page.items) || [];
  const totalUsers = query.data?.pages[query.data.pages.length - 1]?.totalItems || 0;

  return {
    // Query state
    ...query,

    // Processed data
    allUsers,
    totalUsers,

    // Actions
    loadMoreUsers,

    // Loading states
    isLoadingMore: query.isFetchingNextPage,
    canLoadMore: query.hasNextPage
  };
}

export const useInvalidateUserSearch = () => {
  const queryClient = useQueryClient();

  return (searchTerm: string) => {
    return queryClient.invalidateQueries({
      queryKey: [QK_USERS_INFINITE_SEARCH, searchTerm]
    });
  };
};
